import { HttpClient as AngularHttpClient, HttpErrorResponse, HttpXhrBackend } from '@angular/common/http';
import { Bad, BadVal } from '@core/model/framework/Result/BadResult';
import { Ok, OkVal } from '@core/model/framework/Result/OkResult';
import { BadHttpResponse, HttpClient, HttpHeaders, OkHttpResponse } from '@core/model/framework/httpClient';
import { injectable } from 'inversify';
import { firstValueFrom } from 'rxjs';
import { assert } from 'ts-essentials';

@injectable()
export class AngularClient implements HttpClient {
  private httpClient: AngularHttpClient;
  baseUrl: string;

  constructor(baseUrl: string) {
    this.httpClient = new AngularHttpClient(
      new HttpXhrBackend({
        build: () => new XMLHttpRequest(),
      })
    );
    this.baseUrl = baseUrl;
  }

  async post<TResponse, TBody>(
    url: string,
    body: TBody,
    headers?: HttpHeaders
  ): Promise<OkVal<OkHttpResponse<TResponse>> | BadVal<string, BadHttpResponse>> {
    const fullUrl = this.buildUrl(url);
    const httpHeaders = this.setHeaders(headers);

    try {
      const result = await firstValueFrom(
        this.httpClient.post<TResponse>(fullUrl, body, {
          observe: 'response',
          headers: httpHeaders,
        })
      );

      assert(result.body, 'httpClient response should be defined.');
      const val: OkHttpResponse<TResponse> = {
        status: result.status,
        data: result.body,
      };

      return Ok(val);
    } catch (error) {
      const httpErrorResponse = error as HttpErrorResponse;
      const val: BadHttpResponse = {
        status: httpErrorResponse.status,
        data: httpErrorResponse.error,
      };

      return Bad(`ANGULAR_HTTP_ERROR: `, val);
    }
  }

  async put<TResponse, TBody>(
    url: string,
    body: TBody,
    headers?: HttpHeaders
  ): Promise<OkVal<OkHttpResponse<TResponse>> | BadVal<string, BadHttpResponse>> {
    const fullUrl = this.buildUrl(url);
    const httpHeaders = this.setHeaders(headers);

    try {
      const result = await firstValueFrom(
        this.httpClient.put<TResponse>(fullUrl, body, {
          observe: 'response',
          headers: httpHeaders,
        })
      );

      assert(result.body, 'httpClient response should be defined.');
      const val: OkHttpResponse<TResponse> = {
        status: result.status,
        data: result.body,
      };

      return Ok(val);
    } catch (error) {
      const httpErrorResponse = error as HttpErrorResponse;
      const val: BadHttpResponse = {
        status: httpErrorResponse.status,
        data: httpErrorResponse.error,
      };

      return Bad(`ANGULAR_HTTP_ERROR: `, val);
    }
  }

  async get<TResponse>(
    url: string,
    headers?: HttpHeaders
  ): Promise<OkVal<OkHttpResponse<TResponse>> | BadVal<string, BadHttpResponse>> {
    const fullUrl = this.buildUrl(url);
    const httpHeaders = this.setHeaders(headers);

    try {
      const result = await firstValueFrom(
        this.httpClient.get<TResponse>(fullUrl, {
          observe: 'response',
          headers: httpHeaders,
        })
      );

      assert(result.body, 'httpClient response should be defined.');
      const val: OkHttpResponse<TResponse> = {
        status: result.status,
        data: result.body,
      };

      return Ok(val);
    } catch (error) {
      const httpErrorResponse = error as HttpErrorResponse;
      const val: BadHttpResponse = {
        status: httpErrorResponse.status,
        data: httpErrorResponse.error,
      };

      return Bad(`ANGULAR_HTTP_ERROR: `, val);
    }
  }

  private buildUrl(endpoint: string): string {
    if (endpoint.startsWith('/')) {
      return `${this.baseUrl}${endpoint}`;
    }

    throw new Error(`AngularClient - buildUrl - URL doesn't start with /`);
  }

  private setHeaders(customHeader: Record<string, string> | undefined): Record<string, string> {
    // In jsdom test environment it's not allowed to modify Pragma, Expires and Cache-Control headers
    const isTestEnv = process.env.NODE_ENV === 'test';
    const headers = isTestEnv
      ? {
          ...customHeader,
        }
      : {
          ...customHeader,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
        };

    return headers;
  }
}
